import { type ComponentType } from "react";
import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { type SxProps, type Theme } from "@mui/material/styles";
import { TRANSITIONS } from "./constants";

export type NavigationIcon =
  | IconDefinition
  | string
  | ComponentType<Record<string, never>>;

export interface NavigationItemType {
  label: string;
  href: string;
  icon?: NavigationIcon;
  activeMatcher?: (path: string, search: string) => boolean;
  description?: string;
  onClick?: () => void;
  variant?: "default" | "highlight";
  id?: string;
}

export interface NavigationSection {
  label?: string;
  items: NavigationItemType[];
}

export interface FlattenedNavItem extends NavigationItemType {
  section?: string;
  sectionIndex?: number;
  itemIndex: number;
  isStandalone?: boolean;
  isBottom?: boolean;
}

export interface NavigationConfig {
  sections: NavigationSection[];
  standaloneLinks?: NavigationItemType[];
  bottomLinks?: NavigationItemType[];
}

export function shouldShowDivider(
  item: FlattenedNavItem,
  index: number,
  items: FlattenedNavItem[],
  isExpanded: boolean
): boolean {
  if (index === 0 || isExpanded) return false;
  const prevItem = items[index - 1];
  return (
    (prevItem.isStandalone && !item.isStandalone && !item.isBottom) ||
    (prevItem.sectionIndex !== undefined &&
      item.sectionIndex !== undefined &&
      prevItem.sectionIndex !== item.sectionIndex &&
      !item.isBottom)
  );
}

export function shouldShowSectionLabel(
  item: FlattenedNavItem,
  index: number,
  items: FlattenedNavItem[],
  isExpanded: boolean
): boolean {
  if (!item.section || !isExpanded) return false;
  return index === 0 || items[index - 1].section !== item.section;
}

export function getSectionStyles(
  item: FlattenedNavItem,
  isExpanded: boolean,
  navigationConfig: NavigationConfig,
  isFirstInSection: boolean = false
) {
  const baseStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    transition: TRANSITIONS.PADDING,
  };

  if (item.isStandalone) {
    return { ...baseStyles, marginTop: isExpanded ? "24px" : "4px" };
  }

  if (item.isBottom) {
    return {
      ...baseStyles,
      borderTop: "1px solid #e5e7eb",
      marginTop: "auto",
      paddingTop: isExpanded ? "24px" : "8px",
    };
  }

  const sectionIndex = item.sectionIndex ?? 0;
  const hasStandaloneBefore =
    navigationConfig.standaloneLinks &&
    navigationConfig.standaloneLinks.length > 0;

  // Only add padding when it's the first item of a section (new section)
  if (isFirstInSection) {
    if (sectionIndex === 0 && hasStandaloneBefore && isExpanded) {
      return { ...baseStyles, paddingTop: "24px" };
    }

    if (sectionIndex > 0) {
      const styles: SxProps<Theme> = {
        ...baseStyles,
        paddingTop: isExpanded ? "24px" : "8px",
        ...(!isExpanded && { borderTop: "1px solid #e5e7eb" }),
      };
      return styles;
    }
  }

  return baseStyles;
}

export function flattenNavigation(
  config: NavigationConfig
): FlattenedNavItem[] {
  const items: FlattenedNavItem[] = [];

  if (config.standaloneLinks) {
    config.standaloneLinks.forEach((item, index) => {
      items.push({
        ...item,
        isStandalone: true,
        itemIndex: index,
      });
    });
  }

  config.sections.forEach((section, sectionIndex) => {
    section.items.forEach((item, itemIndex) => {
      items.push({
        ...item,
        section: section.label,
        sectionIndex,
        itemIndex,
      });
    });
  });

  if (config.bottomLinks) {
    config.bottomLinks.forEach((item, index) => {
      items.push({
        ...item,
        isBottom: true,
        itemIndex: index,
      });
    });
  }

  return items;
}
